import { useEffect, useState } from "react";
import {
  Star, Loader2, Upload, Flag, X, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

const BUSINESS_TAGS = ["Friendly", "Good Atmosphere", "Great Service", "Cigar Friendly", "Recommended"];
const VENDOR_TAGS = ["Friendly", "Helpful", "Safe", "On Time", "Great Service"];
const REPORT_REASONS = ["Inaccurate", "Abusive", "Spam", "Other"];

/** Submit-a-review modal. */
export function ReviewModal({ open, onOpenChange, target, onSubmitted }) {
  const [stars, setStars] = useState(0);
  const [note, setNote] = useState("");
  const [tags, setTags] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const tagPool = target?.target_type === "vendor" ? VENDOR_TAGS : BUSINESS_TAGS;

  const reset = () => { setStars(0); setNote(""); setTags([]); setPhotos([]); };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => [...p, data.urls]);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (stars < 1) { toast.error("Choose a star rating"); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        target_id: target.target_id,
        target_type: target.target_type,
        stars,
        note: note.trim() || null,
        tags,
        photos,
      });
      toast.success("Thanks, your review has been submitted for approval");
      reset();
      onSubmitted?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-md bg-surface border-gold-soft" data-testid="review-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-foreground">Leave a review</DialogTitle>
          <DialogDescription className="text-foreground/55 text-xs">
            For <span className="text-foreground">{target?.target_name}</span> · your review will be reviewed by an admin before publishing.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="flex flex-col items-center gap-2" data-testid="star-row">
            <div className="eyebrow">Rating</div>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setStars(n)} data-testid={`star-${n}`}
                  className={`p-1 transition-transform hover:scale-110 ${n <= stars ? "text-gold" : "text-foreground/25"}`}>
                  <Star className="w-9 h-9" fill={n <= stars ? "currentColor" : "none"} strokeWidth={1.25} />
                </button>
              ))}
            </div>
          </div>

          <label className="block">
            <div className="eyebrow mb-1.5">Note (optional, 300 chars)</div>
            <textarea data-testid="review-note" rows={3} maxLength={300} className="form-input"
              value={note} onChange={(e) => setNote(e.target.value)} placeholder="A short note for other travelers…" />
            <div className="text-[10px] text-foreground/45 font-mono mt-1 text-right">{note.length}/300</div>
          </label>

          <div>
            <div className="eyebrow mb-1.5">Tags</div>
            <div className="flex flex-wrap gap-2" data-testid="review-tags">
              {tagPool.map((t) => {
                const active = tags.includes(t);
                return (
                  <button key={t} onClick={() => setTags((ts) => active ? ts.filter(x => x !== t) : [...ts, t])}
                    data-testid={`tag-${t.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`px-3 py-1.5 rounded-full text-[11px] border ${
                      active ? "bg-gold text-[hsl(var(--primary-foreground))] border-transparent" : "border-gold-soft text-foreground/65 hover:text-foreground"
                    }`}>{t}</button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="eyebrow mb-1.5">Photos (optional)</div>
            <div className="flex flex-wrap gap-2" data-testid="review-photos">
              {photos.map((p, i) => {
                const src = p.medium || p.large || p.thumbnail;
                const url = src?.startsWith?.("http") ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`;
                return (
                  <div key={i} className="relative w-16 h-16 rounded overflow-hidden border border-gold-soft">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setPhotos((ps) => ps.filter((_, idx) => idx !== i))}
                      data-testid={`review-photo-remove-${i}`}
                      className="absolute top-0.5 right-0.5 w-5 h-5 grid place-items-center rounded-full bg-background/80 text-foreground/65 hover:text-destructive">
                      <X className="w-3 h-3" strokeWidth={1.5}/>
                    </button>
                  </div>
                );
              })}
              <label className="w-16 h-16 rounded border border-dashed border-gold-soft grid place-items-center cursor-pointer text-foreground/55 hover:text-gold" data-testid="review-photo-upload">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5}/> : <Upload className="w-4 h-4" strokeWidth={1.5}/>}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} disabled={uploading} />
              </label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="btn-outline py-2 px-4 text-xs" data-testid="review-cancel">Cancel</button>
          <button onClick={submit} disabled={submitting || stars < 1} className="btn-gold py-2 px-4 text-xs" data-testid="review-submit">
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : null} Submit
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Report-a-review modal. */
export function ReportReviewModal({ open, onOpenChange, review, onReported }) {
  const [reason, setReason] = useState("Inaccurate");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/reviews/${review.id}/report`, { reason });
      toast.success("Thanks, we have received your report");
      onReported?.();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-surface border-gold-soft" data-testid="report-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-foreground">Report review</DialogTitle>
          <DialogDescription className="text-foreground/55 text-xs">
            Admin moderators will review your report.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          <div className="eyebrow mb-1.5">Reason</div>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger data-testid="report-reason"><SelectValue /></SelectTrigger>
            <SelectContent>{REPORT_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="btn-outline py-2 px-4 text-xs" data-testid="report-cancel">Cancel</button>
          <button onClick={submit} disabled={submitting} className="btn-danger py-2 px-4 text-xs" data-testid="report-submit">
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Flag className="w-3.5 h-3.5" strokeWidth={1.5}/>}
            Submit report
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Card displaying a single approved review with a Report flag. */
export function ReviewCard({ review }) {
  const [reportOpen, setReportOpen] = useState(false);
  return (
    <article className="lux-card p-4" data-testid={`review-card-${review.id}`}>
      <div className="flex items-start gap-3">
        {review.reviewer?.profile_photo ? (
          <img src={review.reviewer.profile_photo} alt="" className="w-10 h-10 rounded-full object-cover ring-1 ring-[rgba(232,184,75,0.4)]" />
        ) : (
          <span className="w-10 h-10 rounded-full grid place-items-center bg-[hsl(var(--surface-elevated))] text-gold text-xs border border-gold-soft">
            {(review.reviewer?.display_name || "?").slice(0, 2).toUpperCase()}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-medium text-foreground truncate">{review.reviewer?.display_name || "Traveler"}</div>
            <div className="flex items-center gap-0.5" data-testid={`review-stars-${review.id}`}>
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} className={`w-3.5 h-3.5 ${n <= (review.stars || 0) ? "text-gold fill-gold" : "text-foreground/20"}`} strokeWidth={1.5}/>
              ))}
            </div>
            <button onClick={() => setReportOpen(true)} data-testid={`review-report-btn-${review.id}`}
              className="ml-auto text-foreground/35 hover:text-amber-300 transition-colors p-1" title="Report this review">
              <Flag className="w-3.5 h-3.5" strokeWidth={1.5}/>
            </button>
          </div>
          <div className="text-[11px] font-mono text-foreground/55 mt-0.5">{(review.created_at || "").slice(0, 10)}</div>
          {review.note && <p className="text-sm text-foreground/85 mt-2 leading-relaxed">{review.note}</p>}
          {review.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {review.tags.map((t) => (
                <span key={t} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-gold/10 text-gold border border-gold-soft">{t}</span>
              ))}
            </div>
          )}
          {review.photos?.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mt-3">
              {review.photos.map((p, i) => {
                const src = p.medium || p.large || p.thumbnail;
                const url = src?.startsWith?.("http") ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`;
                return <img key={i} src={url} alt="" className="w-full h-20 object-cover rounded" loading="lazy" />;
              })}
            </div>
          )}
        </div>
      </div>
      <ReportReviewModal open={reportOpen} onOpenChange={setReportOpen} review={review} />
    </article>
  );
}

/** Section that lists approved reviews + a Leave Review button. */
export function ReviewsSection({ targetId, targetType, targetName, canReview, refreshKey = 0, ratingsVisible, approvedCount }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => {
    api.get(`/reviews?target_id=${targetId}&target_type=${targetType}`)
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [targetId, targetType, refreshKey]);

  if (rows === null) return null;
  // Render the section if there's anything to show OR the user can leave one,
  // so we can always surface the 3-review-minimum hint on quiet profiles.
  const count = typeof approvedCount === "number" ? approvedCount : rows.length;
  const showRatingsHint = ratingsVisible === false && count < 3;
  if (rows.length === 0 && !canReview && !showRatingsHint) return null;

  return (
    <section className="px-5 sm:px-6 mb-8" data-testid="reviews-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl text-foreground">Reviews</h2>
        {canReview && (
          <button onClick={() => setOpen(true)} data-testid="leave-review-btn" className="btn-gold py-2 px-3 text-xs">
            <Star className="w-3.5 h-3.5" strokeWidth={1.5}/> Leave review
          </button>
        )}
      </div>
      {showRatingsHint && (
        <div className="lux-card p-3 mb-3 text-[11px] text-foreground/55 font-mono" data-testid="ratings-threshold-hint">
          Star ratings appear after <strong className="text-foreground">3 approved reviews</strong> · currently {count}/3
        </div>
      )}
      {rows.length === 0 ? (
        <div className="lux-card p-6 text-center text-sm text-foreground/55" data-testid="reviews-empty">
          No reviews yet — be the first.
        </div>
      ) : (
        <div className="space-y-3" data-testid="reviews-list">
          {rows.map((r) => <ReviewCard key={r.id} review={r} />)}
        </div>
      )}
      <ReviewModal
        open={open}
        onOpenChange={setOpen}
        target={{ target_id: targetId, target_type: targetType, target_name: targetName }}
        onSubmitted={load}
      />
    </section>
  );
}
